import * as React from 'react';
import { AttachStatus } from '../containers/Request/RequestForm';
import { IFormStateValue } from '../utils/formUtils';

interface AttachmentRules {
  attachment?: {
    forAll?: AttachStatus;
    responses?: Array<{
      label_id: number;
      response: string;
      status: AttachStatus;
    }>;
  };
}

/**
 * Custom hook for calculating attachment status based on form rules and state
 *
 * @param rules - Parsed form rules object
 * @param formState - Current form state
 * @returns Current attachment status
 */
export function useAttachmentStatus(
  rules: AttachmentRules,
  formState: Record<string, IFormStateValue>
): AttachStatus {
  return React.useMemo<AttachStatus>(() => {
    if (!rules?.attachment) return AttachStatus.Optional;

    const { forAll, responses } = rules.attachment;

    // Global override
    if (forAll === AttachStatus.Required) return AttachStatus.Required;
    if (forAll === AttachStatus.NotAllowed) return AttachStatus.NotAllowed;

    // Conditional overrides
    if (!responses || !Array.isArray(responses)) return AttachStatus.Optional;

    for (const { label_id, response: expected, status } of responses) {
      const actual = formState[label_id]?.value;
      if (actual === expected) {
        return status as AttachStatus;
      }
    }

    return AttachStatus.Optional;
  }, [rules, formState]);
}
